import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
    DocumentGetRoot,
    DocumentListFolder,
    DocumentCreateFolder,
    DocumentUploadFile,
    DocumentDownloadFile,
    DocumentRename,
    DocumentDeleteFolder,
    DocumentMove
} from 'services/documentService';
import { Modal, Input, Button, Table, Breadcrumb, Dropdown, Menu, Upload, Space } from 'antd';
import {
    FolderOutlined,
    FileOutlined,
    DownloadOutlined,
    DeleteOutlined,
    EditOutlined,
    MoreOutlined,
    UploadOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import ErrorModal from "popup/ErrorModal";
import { useConfirm } from "popup/ConfirmContext";
import {DocumentItem} from "models/model";
export interface ConfirmOptions {
    title: string;
    description?: string;
}

export default function DocumentManager( { courseId }: { courseId: string }) {
    const { confirm } = useConfirm();
    const [currentPath, setCurrentPath] = useState<string>('');
    const [breadcrumbPaths, setBreadcrumbPaths] = useState<{ title: string; path: string }[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<DocumentItem | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [id, setId] = useState<string>('');


    const [createFolderVisible, setCreateFolderVisible] = useState<boolean>(false);
    const [renameModalVisible, setRenameModalVisible] = useState<boolean>(false);
    const [moveModalVisible, setMoveModalVisible] = useState<boolean>(false);

    // Form states
    const [folderName, setFolderName] = useState<string>('');
    const [newName, setNewName] = useState<string>('');
    const [targetPath, setTargetPath] = useState<string>('');

    // Initialize with root folder
    useEffect(() => {
        loadRootFolder();
    }, [courseId]);

    const loadRootFolder = async () => {
        try {
            if (id === courseId) return;
            setId(courseId);
            setLoading(true);
            const response = await DocumentGetRoot(courseId);
            if (response.result) {
                const rootFolder = response.result;
                setCurrentPath(rootFolder.url);
                setBreadcrumbPaths([{ title: rootFolder.name, path: rootFolder.url }]);
                await loadDocuments(rootFolder.url);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const loadDocuments = async (path: string) => {
        try {
            setLoading(true);
            const response = await DocumentListFolder({ courseId, path });
            if (response.result) {
                setDocuments(response.result);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleNavigate = async (item: DocumentItem) => {
        if (!item.isFolder) return;

        const newPaths = [...breadcrumbPaths];
        const existingIndex = newPaths.findIndex(p => p.path === item.url);

        if (existingIndex >= 0) {
            newPaths.splice(existingIndex + 1);
        } else {
            newPaths.push({ title: item.name, path: item.url });
        }

        setBreadcrumbPaths(newPaths);
        setCurrentPath(item.url);
        await loadDocuments(item.url);
    };

    const handleCreateFolder = async () => {
        if (!folderName.trim()) {
            toast.error('Vui lòng nhập tên thư mục');
            return;
        }

        try {
            setLoading(true);
            const response = await DocumentCreateFolder({
                courseId,
                parentPath: currentPath,
                folderName: folderName.trim()
            });

            if (response.message) {
                toast.success(response.message.toString());
                setCreateFolderVisible(false);
                setFolderName('');
                await loadDocuments(currentPath);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload: UploadProps['onChange'] = async (info) => {
        if (info.file.status === 'done') {
            const { response } = info.file;
            if (response.result) {
                toast.success(`${info.file.name} tải lên thành công`);
                await loadDocuments(currentPath);
            } else {
                toast.error(response?.message || 'Tải lên thất bại');
            }
        } else if (info.file.status === 'error') {
            toast.error(`${info.file.name} tải lên thất bại`);
        }
    };

    const customUpload = async (options: any) => {
        const { file, onSuccess, onError } = options;

        try {
            const response = await DocumentUploadFile({
                courseId,
                name: file.name,
                targetPath: currentPath,
                file
            });

            if (response.result) {
                onSuccess(response, file);
            } else {
                onError(new Error(response.message));
            }
        } catch (error) {
            onError(error);
        }
    };

    const handleDownload = async (item: DocumentItem) => {
        try {
            setLoading(true);
            const blob = await DocumentDownloadFile({
                courseId,
                filePath: item.url
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleRename = async () => {
        if (!selectedItem || !newName.trim()) {
            toast.error('Vui lòng nhập tên mới');
            return;
        }

        try {
            setLoading(true);
            const response = await DocumentRename({
                courseId,
                currentPath: selectedItem.url,
                newName: newName.trim()
            });

            if (response.result) {
                toast.success('Đổi tên thành công');
                setRenameModalVisible(false);
                setNewName('');
                await loadDocuments(currentPath);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleMove = async () => {
        if (!selectedItem || !targetPath.trim()) {
            toast.error('Vui lòng chọn thư mục đích');
            return;
        }

        try {
            setLoading(true);
            const response = await DocumentMove({
                courseId,
                currentPath: selectedItem.url,
                targetFolderPath: targetPath
            });

            if (response.result) {
                toast.success('Di chuyển thành công');
                setMoveModalVisible(false);
                setTargetPath('');
                await loadDocuments(currentPath);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (item: DocumentItem) => {
        const isConfirmed = await confirm({
            title: 'Xác nhận xóa',
            description: `Bạn có chắc chắn muốn xóa ${item.isFolder ? 'thư mục' : 'tệp'} này?`
        });

        if (!isConfirmed) return;

        try {
            setLoading(true);
            const response = await DocumentDeleteFolder({
                courseId,
                itemPath: item.url
            });

            if (response.result) {
                toast.success('Xóa thành công');
                await loadDocuments(currentPath);
            }
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Tên',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: DocumentItem) => (
                <div
                    style={{ display: 'flex', alignItems: 'center', cursor: record.isFolder ? 'pointer' : 'default' }}
                    onClick={() => record.isFolder && handleNavigate(record)}
                >
                    {record.isFolder ? <FolderOutlined style={{ color: '#1890ff', fontSize: 18 }} /> : <FileOutlined style={{ fontSize: 18 }} />}
                    <span style={{ marginLeft: 8 }}>{text}</span>
                </div>
            ),
        },
        {
            title: 'Ngày sửa',
            dataIndex: 'updateDate',
            key: 'updateDate',
            width: 200,
            render: (date: string) => new Date(date).toLocaleString(),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 120,
            render: (_: any, record: DocumentItem) => (
                <Dropdown
                    overlay={
                        <Menu>
                            {!record.isFolder && (
                                <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>
                                    Tải xuống
                                </Menu.Item>
                            )}
                            <Menu.Item key="rename" icon={<EditOutlined />} onClick={() => {
                                setSelectedItem(record);
                                setNewName(record.name);
                                setRenameModalVisible(true);
                            }}>
                                Đổi tên
                            </Menu.Item>
                            <Menu.Item key="move" icon={<ArrowRightOutlined />} onClick={() => {
                                setSelectedItem(record);
                                setMoveModalVisible(true);
                            }}>
                                Di chuyển
                            </Menu.Item>
                            <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)}>
                                Xóa
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'start' }}>
                <Space>
                    <Button
                        type="primary"
                        onClick={() => setCreateFolderVisible(true)}
                        icon={<FolderOutlined style={{fontSize: '20px'}} />}
                        style={{ fontSize: '16px', padding: '8px 16px', height: 'auto' }}
                    >
                        Thư mục mới
                    </Button>

                    <Upload
                        customRequest={customUpload}
                        onChange={handleUpload}
                        showUploadList={false}
                    >
                        <Button
                            type="primary"
                            icon={<UploadOutlined style={{fontSize: '20px'}}/>}
                            style={{ fontSize: '16px', padding: '8px 16px', height: 'auto' }}
                        >
                            Tải tệp lên
                        </Button>
                    </Upload>
                </Space>
                <Breadcrumb style={{display: 'flex', alignItems: 'center', height: 'auto' }}>
                    {breadcrumbPaths.map((path, index) => (
                        <Breadcrumb.Item key={index}>
                            <a onClick={() => handleNavigate({
                                isFolder: true,
                                url: path.path,
                                name: path.title,
                                id: '',
                                creationDate: '',
                                updateDate: ''
                            } as DocumentItem)}>
                                {path.title}
                            </a>
                        </Breadcrumb.Item>
                    ))}
                </Breadcrumb>
            </div>

            <Table
                columns={columns}
                dataSource={documents}
                rowKey="url"
                loading={loading}
                pagination={false}
                locale={{ emptyText: 'Không có tài liệu nào.' }}
            />

            <Modal
                title="Tạo thư mục mới"
                open={createFolderVisible}
                onOk={handleCreateFolder}
                onCancel={() => {
                    setCreateFolderVisible(false);
                    setFolderName('');
                }}
                confirmLoading={loading}
            >
                <Input
                    placeholder="Nhập tên thư mục"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    onPressEnter={handleCreateFolder}
                />
            </Modal>

            <Modal
                title="Đổi tên"
                open={renameModalVisible}
                onOk={handleRename}
                onCancel={() => {
                    setRenameModalVisible(false);
                    setNewName('');
                }}
                confirmLoading={loading}
            >
                <Input
                    placeholder="Nhập tên mới"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onPressEnter={handleRename}
                />
            </Modal>

            <Modal
                title="Di chuyển"
                open={moveModalVisible}
                onOk={handleMove}
                onCancel={() => {
                    setMoveModalVisible(false);
                    setTargetPath('');
                }}
                confirmLoading={loading}
            >
                <Input
                    placeholder="Nhập đường dẫn thư mục đích"
                    value={targetPath}
                    onChange={(e) => setTargetPath(e.target.value)}
                    onPressEnter={handleMove}
                />
            </Modal>

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />
        </div>
    );
}